import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Shuffle, Timer, Plus, Calendar, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useJackpotsList } from '@/api/extensions/useJackpotOps';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { formatDateRange } from '@/utils/formatters';
import { DrawnOutWarningCard } from '@/components/dashboard/DrawnOutWarningCard';

export default function DelegateDashboardPage() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const role = useAuthStore((s) => s.role);

  const { data: jackpotsData, isLoading } = useJackpotsList({ pageSize: 100 });
  const jackpots = jackpotsData?.data ?? [];

  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    let totalEvents = 0;
    let published = 0;
    let upcoming = 0;

    for (const j of jackpots) {
      totalEvents += (j as any).events?.length ?? 0;
      if ((j as any).is_published) published++;
      const start = ((j as any).start_date ?? '').substring(0, 10);
      if (start >= todayStr) upcoming++;
    }

    return {
      totalJackpots: jackpots.length,
      totalEvents,
      published,
      upcoming,
    };
  }, [jackpots]);

  const recentJackpots = useMemo(() => {
    return [...jackpots]
      .sort((a: any, b: any) => {
        const aDate = a.updated_at ?? a.created_at ?? '';
        const bDate = b.updated_at ?? b.created_at ?? '';
        return bDate.localeCompare(aDate);
      })
      .slice(0, 5);
  }, [jackpots]);

  const displayName = (profile as any)?.first_name ?? (profile as any)?.display_first_name ?? 'Delegate';

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Welcome, {displayName}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs capitalize">
              {role?.role_name ?? 'delegate'}
            </Badge>
            Delegate Dashboard
          </p>
        </div>
      </div>

      {/* Drawn-out cancellation reminder — self-hides when nothing pending */}
      <DrawnOutWarningCard />

      {/* Stats */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-2.5">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalJackpots}</p>
                <p className="text-xs text-muted-foreground">Total Jackpots</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-blue-500/10 p-2.5">
                <Calendar className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalEvents}</p>
                <p className="text-xs text-muted-foreground">Total Events</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-green-500/10 p-2.5">
                <Trophy className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.published}</p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-full bg-orange-500/10 p-2.5">
                <Calendar className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.upcoming}</p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/delegate/jackpots?create=true')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2.5">
              <Plus className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">Create Jackpot</h3>
              <p className="text-xs text-muted-foreground">Start a new jackpot or rodeo</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/delegate/draws')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="rounded-full bg-blue-500/10 p-2.5">
              <Shuffle className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">Manage Draws</h3>
              <p className="text-xs text-muted-foreground">Execute and manage team draws</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/delegate/scoring')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="rounded-full bg-green-500/10 p-2.5">
              <Timer className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">Live Scoring</h3>
              <p className="text-xs text-muted-foreground">Enter scores and view standings</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>
      </div>

      {/* Recent Jackpots */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Recent Jackpots</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/delegate/jackpots')}>
            View All
          </Button>
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : recentJackpots.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No jackpots yet. Create your first one!
          </p>
        ) : (
          <div className="space-y-2">
            {recentJackpots.map((j: any) => (
              <Card
                key={j.id}
                className="cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => navigate(`/delegate/jackpots?edit=${j.id}`)}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{j.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateRange(j.start_date, j.end_date)}
                        {' | '}
                        {(j.events?.length ?? 0)} events
                      </p>
                    </div>
                  </div>
                  <Badge variant={j.is_published ? 'default' : 'secondary'} className="text-xs shrink-0">
                    {j.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
